import { JSX, useContext, useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { createRoutesFromChildren, matchRoutes } from 'react-router';
import * as Sentry from '@sentry/react';
import type { BrowserOptions } from '@sentry/browser';
import { AppContext } from './App';
import useGetCurrentUser from '../features/users/services/useGetCurrentUser';

export const sentryOptions: BrowserOptions = {
  debug: false,
  enabled: 'true'.localeCompare(import.meta.env.VITE_SENTRY_ENABLED) === 0,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV,
  release: `ap-events-backend@${import.meta.env.VITE_VERSION}`,
  /**
   * It controls for which URLs distributed tracing should be enabled
   * TODO: add import.meta.env.VITE_API_URL when NestJS will be ready
   *  <!> if the env variable is passed but empty, the site will crash because the Sentry headers will be passed to
   *  the IDP and rejected by the CORS policy
   */
  tracePropagationTargets: [
    'localhost',
    ...(import.meta.env.VITE_BOOKING_URL ? [import.meta.env.VITE_BOOKING_URL] : []),
    ...(import.meta.env.VITE_HASURA_API_URL ? [import.meta.env.VITE_HASURA_API_URL] : []),
    ...(import.meta.env.VITE_BASE_URL ? [import.meta.env.VITE_BASE_URL] : []),
  ],
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  normalizeDepth: 5,
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
  replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
  replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === 'console' && breadcrumb.message && breadcrumb.message.startsWith('%c')) {
      // Exclude AppConsole console output
      return null;
    }
    return breadcrumb;
  },
};

interface Props {
  children: JSX.Element;
}

function AppSentryProvider(props: Props) {
  const { user } = useContext(AppContext);
  const currentUser = useGetCurrentUser();

  useEffect(() => {
    if (user && currentUser) {
      Sentry.setUser({
        id: user.id,
        username: currentUser._id,
        segment: currentUser.roles.map((role) => role.toString()).join(', '),
      });
    }
  }, [user?.id]);

  return <Sentry.ErrorBoundary>{props.children}</Sentry.ErrorBoundary>;
}

export default Sentry.withProfiler(AppSentryProvider);
